import { merge } from 'lodash'
import { Components, createTheme, Theme, ThemeOptions } from '@mui/material/styles'
import type { TypographyOptions } from '@mui/material/styles/createTypography'
import { ThemeBrandAssetsType, ThemeBrandColorsType, ThemeBrandTypographyType } from './types'
import { defaultAssets, defaultColors, defaultTypography } from './defaultTheme'
import { ProductName } from '../../types/interface/quote.interface'

type BrandThemeConfig = Partial<
  Pick<BrandTheme, 'colors' | 'typography' | 'assets' | 'overrides' | 'productName'>
>

// v4 theme `overrides` / `props` maps become v5 `components`. Keys that are not MUI
// component names (brands spread their layout overrides in here) were ignored by v4 too.
const toComponents = (
  styleOverrides: Record<string, unknown>,
  defaultProps: Record<string, Record<string, unknown>>
): Components<Theme> => {
  const components: Record<string, Record<string, unknown>> = {}
  const add = (slot: 'styleOverrides' | 'defaultProps', name: string, value: unknown) => {
    if (name.startsWith('Mui')) components[name] = { ...components[name], [slot]: value }
  }
  Object.entries(styleOverrides).forEach(([name, value]) => add('styleOverrides', name, value))
  Object.entries(defaultProps).forEach(([name, value]) => add('defaultProps', name, value))

  return components as Components<Theme>
}

const breakpointValues = { xs: 0, sm: 321, md: 450, lg: 1025, xl: 1920 }

class BrandTheme {
  productName: ProductName = ProductName.VEHICLE
  colors: ThemeBrandColorsType = defaultColors
  typography: ThemeBrandTypographyType = defaultTypography
  assets: ThemeBrandAssetsType = defaultAssets
  overrides: Record<string, unknown>
  themeProps: Record<string, Record<string, unknown>> = {}
  theme: Theme
  // Used to reinitialize the whole theme through the setup
  initCallback: () => BrandTheme
  // Used to update theme state after theme creation
  createThemeCallback: (theme: Theme) => void = () => {
    /** noop */
  }

  constructor() {
    this.createTheme()
  }

  setCreateThemeCallback(createThemeCallback: (theme: Theme) => void) {
    this.createThemeCallback = createThemeCallback

    return this
  }

  setup(config: BrandThemeConfig) {
    const {
      productName = this.productName,
      colors = this.colors,
      typography = this.typography,
      assets = this.assets,
      overrides = this.assets,
    } = config
    this.productName = productName
    this.colors = colors
    this.typography = typography
    this.assets = assets
    this.overrides = overrides

    this.createTheme()

    return this
  }

  createTheme() {
    const theme = createTheme(
      this.getBrandedTheme(this.colors, this.typography, this.assets, this.overrides)
    )
    // v4 CssBaseline spread typography.body2 onto <body>; v5 uses body1. Keep v4's.
    const baseline = theme.components?.MuiCssBaseline
    if (baseline) {
      // An array is valid CSSInterpolation at runtime; the slot's typing is narrower.
      baseline.styleOverrides = [
        baseline.styleOverrides,
        { body: theme.typography.body2 },
      ] as unknown as typeof baseline.styleOverrides
    }
    this.theme = theme

    this.createThemeCallback(this.theme)
  }

  init(initCallback: () => BrandTheme): BrandTheme {
    this.initCallback = initCallback

    return this.initCallback()
  }

  getBrandedTheme(
    brandColors: ThemeBrandColorsType = this.colors,
    brandTypography: ThemeBrandTypographyType = this.typography,
    brandAssets: ThemeBrandAssetsType = this.assets,
    brandOverrides?: Record<string, unknown>
  ): ThemeOptions {
    return {
      breakpoints: { values: breakpointValues },
      // Default palette mapped with Brand Related Theme Options
      palette: {
        // v5 changed these two defaults (#fff and 0.6 alpha); keep v4's look.
        background: { default: '#fafafa' },
        text: { secondary: 'rgba(0, 0, 0, 0.54)' },
        primary: {
          main: brandColors.main,
          contrastText: brandColors.background,
          dark: brandColors.textMain,
          light: brandColors.background,
        },
        secondary: {
          main: brandColors.headingMain,
          dark: brandColors.textSecondary,
          contrastText: brandColors.clean,
          light: brandColors.clean,
        },
        info: {
          main: brandColors.textPlaceholder,
          contrastText: brandColors.textDisclaimer,
          dark: brandColors.textSecondary,
          light: brandColors.textDisclaimer,
        },
        error: {
          main: brandColors.textError,
          contrastText: brandColors.textDisclaimer,
          dark: brandColors.textSecondary,
          light: brandColors.textDisclaimer,
        },
        success: {
          main: brandColors.main,
          contrastText: brandColors.background,
          dark: brandColors.main,
          light: brandColors.background,
        },
        warning: {
          main: brandColors.textError,
          contrastText: brandColors.textDisclaimer,
          dark: brandColors.textSecondary,
          light: brandColors.textDisclaimer,
        },
      },
      // Brand Related Theme Options
      brand: {
        colors: brandColors,
        typography: brandTypography,
        assets: brandAssets,
      },
      typography: {
        fontFamily: [
          '-apple-system',
          'BlinkMacSystemFont',
          'Segoe UI',
          'Roboto',
          'Oxygen',
          'Ubuntu',
          'Cantarell',
          'Fira Sans',
          'Droid Sans',
          'Helvetica Neue',
          'sans-serif',
          ...brandTypography.extraFonts,
        ].join(','),
        h1: brandTypography.heading.h1,
        h2: brandTypography.heading.h2,
        h3: brandTypography.heading.h3,
        h4: brandTypography.heading.h4,
        h5: brandTypography.heading.h5,
        h6: brandTypography.heading.h6,
        body1: {
          color: brandColors.textMain,
          ...brandTypography.text.normal,
        },
        body2: {
          color: brandColors.textSecondary,
          ...brandTypography.text.small,
        },
        caption: {
          color: brandColors.textDisclaimer,
          ...brandTypography.text.small,
        },
        // Brand styles may nest selectors, which MUI's typography typings do not allow for.
      } as TypographyOptions,
      components: toComponents(
        merge(
          {
            MuiCssBaseline: [
              // One `@font-face` per face: emotion collapses an array value into a single block.
              ...(brandTypography.customFontFaces || []).map((face) => ({ '@font-face': face })),
              {
                code: {
                  fontFamily: [
                    'source-code-pro',
                    'Menlo, Monaco',
                    'Consolas',
                    'Courier New',
                    'monospace',
                    ...brandTypography.extraFonts,
                  ].join(','),
                },
              },
            ],
            MuiCard: {
              root: {
                borderRadius: '8px',
                boxShadow: '0px 6px 15px rgba(13, 51, 32, 0.1)',
              },
            },
            MuiOutlinedInput: {
              root: {
                border: 0,
                borderRadius: '8px',
                boxShadow: '0px 6px 20px rgba(13, 51, 32, 0.1)',
              },
              notchedOutline: {
                border: 0,
              },
              focused: {},
            },
            MuiInputLabel: {
              // v4: long labels wrap; v5 keeps them on one line past the field.
              root: { whiteSpace: 'normal', overflow: 'visible', maxWidth: 'none' },
              shrink: {
                transform: 'translate(0, 1.5px) scale(1)',
              },
            },
            MuiInputBase: {
              root: {
                fontSize: '16px',
              },
              input: {
                '&:invalid': {
                  color: brandColors.textError,
                },
                // v5 greys disabled text via -webkit-text-fill-color, which overrides `color`.
                '&.Mui-disabled': { WebkitTextFillColor: 'currentColor' },
              },
            },
            MuiSelect: {
              select: {
                '&:focus': {
                  backgroundColor: 'transparent',
                },
              },
            },
            MuiMenuItem: {
              root: {
                color: brandColors.textMain,
                fontSize: '1rem',
              },
            },
            MuiRadio: {
              root: {
                color: brandColors.main,
                '&.Mui-checked': {
                  color: brandColors.main,
                },
                // NB: no `'&$checked + $label'` rule here — MuiRadio has no `label` slot, so JSS
                // could never resolve it and warned on every theme creation. The checked label
                // styling lives in RFFFormControlRadio's own `checked` class.
              },
              colorSecondary: {
                color: brandColors.main,
                '&.Mui-checked': {
                  color: brandColors.main,
                },
              },
              checked: {},
            },
            MuiSwitch: {
              colorSecondary: {
                '&.Mui-checked': {
                  color: brandColors.main,
                },
                '&.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: brandColors.main,
                },
              },
              checked: {},
              track: {},
            },
            MuiFormLabel: {
              root: {
                color: brandColors.textSecondary,
                lineHeight: 1, // v5 default 1.4375em moves every form down
                '&.Mui-focused': {
                  color: brandColors.textSecondary,
                },
              },
            },
            // v4 looks that v5 changed: icon-button padding, tab size, and a divider drawn as a
            // 1px background.
            MuiIconButton: { root: { padding: 12 }, sizeSmall: { padding: 3 } },
            MuiTab: {
              root: {
                flexDirection: 'row',
                padding: '6px 12px',
                minWidth: 72,
                maxWidth: 264,
                lineHeight: 1.75,
                [`@media (min-width:${breakpointValues.sm}px)`]: { minWidth: 160 },
              },
            },
            MuiDivider: {
              root: { height: 1, border: 'none', backgroundColor: 'rgba(0, 0, 0, 0.12)' },
              vertical: { height: '100%', width: 1 },
              flexItem: { alignSelf: 'stretch', height: 'auto' },
            },
          },
          // Brands extend the base slots rather than replacing whole components.
          brandOverrides
        ),
        {
          // v4 defaults: `secondary` (the `colorSecondary` overrides above depend on it), `default`
          // (closest v5 value is `inherit`) and hover-only link underline.
          MuiRadio: { color: 'secondary' },
          MuiSwitch: { color: 'secondary' },
          MuiCheckbox: { color: 'secondary' },
          MuiButton: { color: 'inherit' },
          MuiButtonGroup: { color: 'inherit' },
          MuiLink: { underline: 'hover' },
          ...this.themeProps,
        }
      ),
    }
  }
}

export default new BrandTheme()
